import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { Ic } from '../crm/icons';

export interface DropdownOption<T extends string = string> {
  value: T;
  label: string;
  icon?: ReactNode;
  hint?: string;
  /** Render as a destructive action (e.g. Log out). */
  danger?: boolean;
}

export interface DropdownProps<T extends string = string> {
  value: T;
  options: readonly DropdownOption<T>[];
  onChange: (value: T) => void;
  /** Custom trigger content; receives the selected option + open state. */
  trigger?: (ctx: { selected: DropdownOption<T> | undefined; open: boolean }) => ReactNode;
  /** Small heading rendered above the option list. */
  menuLabel?: string;
  /** Rich header block (e.g. name + email) rendered at the top of the menu. */
  menuHeader?: ReactNode;
  /** Menu edge alignment relative to the trigger. */
  align?: 'start' | 'end';
  className?: string;
  buttonClassName?: string;
}

interface Coords {
  top: number;
  left?: number;
  right?: number;
}

/**
 * Reusable single-select dropdown menu for the CRM. The menu is rendered in a
 * PORTAL with fixed positioning, so it always floats above page content and can
 * never be clipped or hidden behind cards/panels (no stacking-context traps).
 * Handles click-outside, Esc, and reposition on scroll/resize. Style lives in
 * `styles/crm.css` under `.ui-dropdown*`. Pair with `<Filter>` for the funnel
 * variant.
 */
export function Dropdown<T extends string = string>({
  value,
  options,
  onChange,
  trigger,
  menuLabel,
  menuHeader,
  align = 'end',
  className = '',
  buttonClassName = '',
}: DropdownProps<T>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const selected = options.find((o) => o.value === value);

  const place = useCallback((): void => {
    const el = triggerRef.current;
    if (el === null) return;
    const r = el.getBoundingClientRect();
    setCoords(
      align === 'end'
        ? { top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) }
        : { top: r.bottom + 8, left: r.left },
    );
  }, [align]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) === true) return;
      if (menuRef.current?.contains(t) === true) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const reposition = (): void => place();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, place]);

  return (
    <div className={`ui-dropdown ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className={`ui-dropdown-trigger ${open ? 'is-open' : ''} ${buttonClassName}`.trim()}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger !== undefined ? (
          trigger({ selected, open })
        ) : (
          <>
            <span className="ui-dropdown-value">{selected?.label ?? menuLabel ?? 'Select'}</span>
            <span className="ui-dropdown-caret">
              <Ic.chev width={14} height={14} strokeWidth={2} />
            </span>
          </>
        )}
      </button>

      {open && coords !== null
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              className={`ui-dropdown-menu ui-dropdown-menu--${align} is-open`}
              style={{ top: coords.top, left: coords.left, right: coords.right }}
            >
              {menuHeader !== undefined && <div className="ui-dropdown-menu-head">{menuHeader}</div>}
              {menuLabel !== undefined && <p className="ui-dropdown-menu-label">{menuLabel}</p>}
              {options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={o.value === value}
                  className={`ui-dropdown-item ${o.value === value ? 'is-selected' : ''} ${o.danger === true ? 'is-danger' : ''}`.trim()}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  {o.icon !== undefined && <span className="ui-dropdown-item-icon">{o.icon}</span>}
                  <span className="ui-dropdown-item-text">
                    <span className="ui-dropdown-item-label">{o.label}</span>
                    {o.hint !== undefined && <span className="ui-dropdown-item-hint">{o.hint}</span>}
                  </span>
                  <span className="ui-dropdown-item-check" aria-hidden="true">
                    <Ic.check width={13} height={13} />
                  </span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
